import styled from "@emotion/styled";
import { Button, Empty, Skeleton } from "antd";
import { ReactNode } from "react";
import { sd } from "../tokens";

interface SectionCardProps {
  /** ป้ายเล็กสีเทาเหนือหัวข้อ เช่น "ผลการขาย" */
  eyebrow?: ReactNode;
  title?: ReactNode;
  /** มุมขวาบนของการ์ด — ปุ่ม/ลิงก์/Segmented */
  extra?: ReactNode;
  loading?: boolean;
  /** จางลงระหว่างโหลดข้อมูลชุดใหม่ (ยังเห็นค่าเดิม) */
  refreshing?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: boolean;
  emptyText?: string;
  /** กันไม่ให้ grid กระตุกตอนสลับ state */
  minHeight?: number;
  skeletonRows?: number;
  children: ReactNode;
}

/**
 * การ์ดขาวของหน้านี้ + จุดเดียวที่ตัดสินใจเรื่อง loading / error / empty
 * error แสดง inline ในการ์ด ไม่ใช้ toast เพราะหน้านี้มี 7 query ถ้าเด้ง toast พร้อมกันจะรก
 */
const SectionCard = ({
  eyebrow,
  title,
  extra,
  loading,
  refreshing,
  error,
  onRetry,
  empty,
  emptyText = "ยังไม่มีข้อมูลในช่วงนี้",
  minHeight,
  skeletonRows = 3,
  children,
}: SectionCardProps) => {
  const renderBody = () => {
    if (loading) {
      return <Skeleton active title={false} paragraph={{ rows: skeletonRows }} />;
    }
    if (error) {
      return (
        <StateBox>
          <StateText>ไม่สามารถโหลดข้อมูลได้</StateText>
          {onRetry ? (
            <Button size="small" type="link" onClick={onRetry}>
              ลองใหม่
            </Button>
          ) : null}
        </StateBox>
      );
    }
    if (empty) {
      return (
        <StateBox>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
        </StateBox>
      );
    }
    return <Body refreshing={!!refreshing}>{children}</Body>;
  };

  return (
    <Card minHeight={minHeight}>
      {(title || extra || eyebrow) && (
        <Head>
          <HeadText>
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            {title ? <Title>{title}</Title> : null}
          </HeadText>
          {extra ? <Extra>{extra}</Extra> : null}
        </Head>
      )}
      {renderBody()}
    </Card>
  );
};

export default SectionCard;

const Card = styled.div<{ minHeight?: number }>`
  background: ${sd.cardBg};
  border: 1px solid ${sd.border};
  border-radius: ${sd.radius}px;
  box-shadow: ${sd.shadow};
  padding: 20px;
  height: 100%;
  min-height: ${(p) => (p.minHeight ? `${p.minHeight}px` : "auto")};
  display: flex;
  flex-direction: column;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const HeadText = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: 12px;
  color: ${sd.subtle};
  margin-bottom: 2px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${sd.ink};
  line-height: 1.3;
`;

const Extra = styled.div`
  flex: 0 0 auto;
`;

const Body = styled.div<{ refreshing: boolean }>`
  flex: 1;
  min-width: 0;
  transition: opacity 0.15s ease;
  opacity: ${(p) => (p.refreshing ? 0.55 : 1)};
  pointer-events: ${(p) => (p.refreshing ? "none" : "auto")};
`;

const StateBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 24px 0;
  color: ${sd.muted};
`;

const StateText = styled.div`
  font-size: 14px;
`;
